import React, { useEffect } from 'react';
import { LinkTypesEnum, DispatchAreaObject, ManageDispatchAreasResource } from '@mark43/cad-api';
import type { ManageDispatchAreasView } from '@mark43/cad-api';
import { createContext, PropsWithChildren, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useApiClient } from '@m43/core';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useCurrentUser } from '../current-user/current-user';
import { createStreamListener, useStream } from '../socket/use-stream';
import { DispatchAreaStore } from './dispatch-area-store';
import { useToast } from '@cad-arc';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { naivePluralize } from '../common/helpers/utils';
import { useApplicationType } from '../common/hooks/use-application-type';
import { electronBridge } from '../electron-bridge/electron-bridge';

export const DispatchAreaStoreContext = createContext<DispatchAreaStore | undefined>(undefined);

const ONE_HOUR = 1000 * 60 * 60;

export function DispatchAreaStoreProvider({
    children,
    initialState,
}: PropsWithChildren<{
    initialState?: ManageDispatchAreasView;
}>): React.ReactElement {
    const { isFirstResponder, isWeb } = useApplicationType();
    const currentUser = useCurrentUser();
    const initialStateFromBootstrap = useBootstrapState()?.manageDispatchAreasView;
    const modalControls = useFullscreenModalControls();
    const apiClient = useApiClient();
    const enabledFeatures = useEnabledFeatures();
    const dispatchAreaStore = useState(
        () => new DispatchAreaStore(initialState ?? initialStateFromBootstrap)
    )[0];

    const toast = useToast();

    const mutation = useMutation(
        (args: { takenDispatchAreaIds: number[]; sentDispatchAreaIds: number[] }) => {
            return apiClient
                .request<ManageDispatchAreasResource.GetManageDispatchAreasView>({
                    method: 'POST',
                    url: 'manage_dispatch_areas',
                    data: { includeAllObjects: true },
                })
                .then((data) => {
                    return [data, args] as const;
                });
        },
        {
            onSuccess([response, args]) {
                if (response.data.data) {
                    const dispatchAreaObjectEntries = response.data.data.userDispatchAreaObjects
                        .map((userDispatchAreaObject) =>
                            userDispatchAreaObject.id
                                ? [userDispatchAreaObject.id, userDispatchAreaObject]
                                : undefined
                        )
                        .filter(Boolean) as [number, DispatchAreaObject][];

                    const newDispatchAreaObjectsById: Record<
                        number,
                        DispatchAreaObject
                    > = Object.fromEntries(dispatchAreaObjectEntries);
                    if (args.sentDispatchAreaIds.length > 0) {
                        const userDisplay =
                            newDispatchAreaObjectsById[args.sentDispatchAreaIds[0]]
                                .linkUpdatedByUser;
                        const dispatchAreasDisplay = naivePluralize(
                            'D-Area',
                            args.sentDispatchAreaIds.length
                        );
                        const dispatchAreaNames = args.sentDispatchAreaIds
                            .map((id) => newDispatchAreaObjectsById[id].name)
                            .join(', ');
                        toast({
                            description: `${userDisplay} sent you ${dispatchAreasDisplay} ${dispatchAreaNames}.`,
                            status: 'default',
                            duration: ONE_HOUR,
                        });
                    }
                    if (args.takenDispatchAreaIds.length > 0) {
                        const newDispatcher =
                            newDispatchAreaObjectsById[args.takenDispatchAreaIds[0]]
                                .linkUpdatedByUser;
                        const dispatchAreasDisplay = naivePluralize(
                            'D-Area',
                            args.takenDispatchAreaIds.length
                        );
                        const takenDispatchAreaNames = args.takenDispatchAreaIds
                            .map((id) => newDispatchAreaObjectsById[id].name)
                            .join(', ');
                        toast({
                            description: `${newDispatcher} took ownership of ${dispatchAreasDisplay} ${takenDispatchAreaNames}. You are now monitoring ${takenDispatchAreaNames}.`,
                            status: 'default',
                            duration: 5000,
                        });
                    }
                    dispatchAreaStore.setManageDispatchAreasView(response.data.data);
                }
            },
        }
    );

    const listener = React.useMemo(
        () =>
            createStreamListener({
                streamNames: ['nudge'],
                onMessage(message) {
                    const dispatchAreaOwnerLinkNudges = message.payload.map['DAOL'];
                    if (dispatchAreaOwnerLinkNudges && dispatchAreaOwnerLinkNudges.length > 0) {
                        const takenDispatchAreaIds: number[] = [];
                        const sentDispatchAreaIds: number[] = [];
                        let relevantDispatchAreaChange: boolean = false;
                        for (const dispatchAreaOwnerLinkNudge of dispatchAreaOwnerLinkNudges) {
                            if (dispatchAreaOwnerLinkNudge.user === currentUser.userId) {
                                relevantDispatchAreaChange = true;
                                // if this update was made by someone else, and i dont already own this, this is a "sent" d-area
                                if (
                                    dispatchAreaOwnerLinkNudge.ub !== currentUser.userId &&
                                    !dispatchAreaStore.ownedDispatchAreasById[
                                        dispatchAreaOwnerLinkNudge.d!
                                    ]
                                ) {
                                    sentDispatchAreaIds.push(dispatchAreaOwnerLinkNudge.d!);
                                }
                            }
                            // if this update was made by someone else, I used to own this, but now I don't,
                            // then this is a "taken" d-area
                            if (
                                dispatchAreaOwnerLinkNudge.ub !== currentUser.userId &&
                                dispatchAreaStore.ownedDispatchAreasById[
                                    dispatchAreaOwnerLinkNudge.d!
                                ] &&
                                dispatchAreaOwnerLinkNudge.user !== currentUser.userId &&
                                dispatchAreaOwnerLinkNudge.lt ===
                                    LinkTypesEnum.DISPATCH_AREA_OWNER_PRIMARY
                            ) {
                                relevantDispatchAreaChange = true;
                                takenDispatchAreaIds.push(dispatchAreaOwnerLinkNudge.d!);
                            }
                        }
                        if (relevantDispatchAreaChange) {
                            mutation.mutate({
                                takenDispatchAreaIds,
                                sentDispatchAreaIds,
                            });
                        }
                    }
                },
                onSocketStatusChange(socketStatusChangeEvent) {
                    if (
                        socketStatusChangeEvent.status === 'CONNECTED' &&
                        socketStatusChangeEvent.timesConnected > 1
                    ) {
                        mutation.mutate({ sentDispatchAreaIds: [], takenDispatchAreaIds: [] });
                    }
                },
            }),
        [currentUser, mutation, dispatchAreaStore]
    );

    useStream(listener);

    useEffect(() => {
        if (isWeb) {
            if (!isFirstResponder && !enabledFeatures.hideManageDispatchAreasModalAtStartup) {
                modalControls.open({
                    type: 'MANAGE_DISPATCH_AREAS',
                    props: {},
                });
            }
        } else if (electronBridge && 'isElectronRewrite' in electronBridge) {
            if (electronBridge && electronBridge.args) {
                const args = electronBridge.args;
                if (!isFirstResponder && args['--open-d-area-modal'] === true) {
                    modalControls.open({
                        type: 'MANAGE_DISPATCH_AREAS',
                        props: {},
                    });
                }
            }
        }
    }, [
        isWeb,
        enabledFeatures.hideManageDispatchAreasModalAtStartup,
        modalControls,
        isFirstResponder,
    ]);

    return (
        <DispatchAreaStoreContext.Provider value={dispatchAreaStore}>
            {children}
        </DispatchAreaStoreContext.Provider>
    );
}
